import React, { useEffect, useCallback, useState } from 'react';
import { translate } from '../Services/translation';

const TakeOver = ({
  desktopImageSrc,
  mobileImageSrc,
  desktopImageAlt,
  mobileImageAlt,
  desktopContent,
  mobileContent,
  linkUrl,
  linkTitle,
  desktopContentPosition,
  mobileContentPosition,
  logoUrl,
  useImageOverlay,
}) => {
  const DENIED_URL_INCLUDES = ['gad_source', 'fbclid'];
  const [shouldBeVisible, setShouldBeVisible] = useState(false);

  const [desktopContentValue, setDesktopContentValue] = useState(null);
  const [mobileContentValue, setMobileContentValue] = useState(null);

  const cookieName = 'userHasClosedTakeover';
  const sessionStorageKey = 'hasVisitedFromDeniedUrl';
  const hasVisitedFromDeniedUrl = useCallback(() => {
    const url = window.location.href;
    return DENIED_URL_INCLUDES.some((deniedUrl) => url.includes(deniedUrl));
  }, []);

  const setSessionStorage = useCallback(() => {
    if (hasVisitedFromDeniedUrl()) {
      try {
        sessionStorage.setItem(sessionStorageKey, 'true');
      } catch (error) {
        console.error('Error setting sessionStorage:', error);
      }
    }
  }, [hasVisitedFromDeniedUrl]);
  useEffect(() => {
    setSessionStorage();
  }, [setSessionStorage]);

  const userHasClosedTakeover = useCallback(() => {
    try {
      const storedData = localStorage.getItem(cookieName);
      if (storedData) {
        const { value, expiresAt } = JSON.parse(storedData);
        const now = new Date();
        if (now > new Date(expiresAt)) {
          ClearHasClosedTakeoverData();
          return false;
        }
        return !!value;
      }
    } catch (error) {
      console.error('Error parsing localStorage data:', error);
      ClearHasClosedTakeoverData();
    }
    return false;
  }, []);

  const ClearHasClosedTakeoverData = () => {
    try {
      localStorage.removeItem(cookieName);
    } catch (error) {
      console.error('Error clearing localStorage data:', error);
    }
  };

  const setTakeoverClosedState = () => {
    const date = new Date();
    date.setTime(date.getTime() + 72 * 60 * 60 * 1000); // Set expiration to 72 hours from now
    const data = {
      value: true,
      expiresAt: date.toISOString(),
    };
    localStorage.setItem(cookieName, JSON.stringify(data));
  };

  useEffect(() => {
    const hasClosedTakeOver = userHasClosedTakeover();
    const deniedUrlSession =
      sessionStorage.getItem(sessionStorageKey) === 'true';
    if (hasClosedTakeOver || deniedUrlSession) {
      setShouldBeVisible(false);
    } else {
      setShouldBeVisible(true);
    }
  }, [userHasClosedTakeover, cookieName]);

  const closeTakeover = () => {
    setTakeoverClosedState();
    setShouldBeVisible(false);
  };

  const closeOnEscape = (e) => {
    if (e.key === 'Escape') {
      closeTakeover();
    }
  };
  const handleLinkClick = () => {
    // We do this to be able to set the cookie before the link is followed instead of using a regular href
    closeTakeover();
    if (linkUrl) {
      window.open(linkUrl, '_self');
    }
  };

  useEffect(() => {
    try {
      if (desktopContent && desktopContent != 'null') {
        setDesktopContentValue(JSON.parse(desktopContent).value);
      }
    } catch (error) {
      console.error('Error parsing desktopContent:', error);
    }
  }, [desktopContent]);

  useEffect(() => {
    try {
      if (mobileContent && mobileContent != 'null') {
        setMobileContentValue(JSON.parse(mobileContent).value);
      }
    } catch (error) {
      console.error('Error parsing mobileContent:', error);
    }
  }, [mobileContent]);

  useEffect(() => {
    const bodyElement = document.body;
    const containerElement = document.getElementById('takeover');

    const addListeners = () => {
      document.addEventListener('keydown', closeOnEscape);
    };

    const removeListeners = () => {
      document.removeEventListener('keydown', closeOnEscape);
    };

    if (shouldBeVisible) {
      bodyElement.classList.add('takeover-visible');
      if (containerElement) {
        containerElement.classList.add('takeover--visible');
      }
      addListeners();
    } else {
      bodyElement.classList.remove('takeover-visible');
      containerElement.classList.remove('takeover--visible');
      removeListeners();
    }

    return () => {
      removeListeners(); // Ensure cleanup
    };
  }, [shouldBeVisible]);

  const Wrapper = linkUrl ? 'a' : 'div';

  if (!shouldBeVisible) {
    return null;
  }
  return (
    <div>
      <header className="takeover__header">
        <button
          onClick={() => closeTakeover()}
          className="takeover__logo-wrapper"
        >
          <img src={logoUrl} className="takeover__logo" />
        </button>
        <button
          className="takeover__close-button"
          onClick={() => closeTakeover()}
          aria-label={translate('takeover.close-button')}
        >
          {translate('takeover.close-button')}
          <i className="fal fa-arrow-right"></i>
        </button>
      </header>
      <Wrapper
        href={linkUrl || undefined}
        className="takeover__wrapper"
        title={linkTitle || undefined}
        {...(linkUrl && { onClick: handleLinkClick })}
      >
        {desktopImageSrc && (
          <img
            className={`takeover__image takeover__image--desktop ${
              useImageOverlay ? 'takeover__image--overlay' : ''
            }`}
            src={desktopImageSrc}
            alt={desktopImageAlt}
          />
        )}
        {(mobileImageSrc || desktopImageSrc) && (
          <img
            className={`takeover__image takeover__image--mobile ${
              useImageOverlay ? 'takeover__image--overlay' : ''
            }`}
            src={mobileImageSrc ? mobileImageSrc : desktopImageSrc}
            alt={mobileImageAlt ? mobileImageAlt : desktopImageAlt}
          />
        )}
        {useImageOverlay && <div className="takeover__image-overlay"></div>}
        <div className="takeover__content">
          <div
            className={`takeover__content--desktop ${desktopContentPosition}`}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: desktopContentValue,
              }}
            ></div>
          </div>
          <div
            className={`takeover__content--mobile takeover__mobile-content ${mobileContentPosition}`}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: mobileContentValue,
              }}
            ></div>
          </div>
        </div>
      </Wrapper>
    </div>
  );
};

export default TakeOver;
